import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { Form, Formik } from 'formik';
import Yup from 'utils/validation/yup-extensions';
import RequiredIndicator from 'pages/ApplicationRequest/components/containers/RequiredIndicator';
import {
	getJobCategory,
	getJobCategoryDependencies,
	searchJobCategories,
	submitJobCategoryForm,
} from '../_api';
import { JobCategoryDTO } from '../_classes';
import {
	BEM_NAME_ELEMENTS,
	JOB_CATEGORY_KEYS,
	JOB_CATEGORY_TYPE_OPTIONS,
	TRAINING_TRACK_MAX,
} from '../_const';
import {
	OBJ_APP_BRIDGES,
	OBJ_APP_HP,
} from 'pages/Bridges/ManagerValidation/_constants';
import FormControlCustomized from '../layouts/FormControlCustomized';
import TrainingTrackAsyncAutocomplete from '../components/TrainingTrackAsyncAutocomplete';
import { nanoid } from 'nanoid';
import ReactMarkdown from 'react-markdown';
/**
 * Create or update a Job Category
 * @param {object} jobCategory job category details (empty if new)
 * @param {boolean} isNew flag for indicating new job category
 * @param {object} maintenanceInformation currently logged in user information
 * @returns job category creation/editing form
 */
const FormView = ({ jobCategory, isNew, maintenanceInformation }) => {
	const [sSubmissionStatus, setSubmissionStatus] = useState(null);
	const [sKey, setKey] = useState(nanoid());
	const [sIsDeactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
	const [sDependencySummary, setDependencySummary] = useState({
		mappingCount: 0,
		userAssignmentCount: 0,
	});
	const [sDependencySummaryError, setDependencySummaryError] =
		useState(false);
	const [sIsDeactivating, setIsDeactivating] = useState(false);
	const [sPendingValues, setPendingValues] = useState(null);
	const history = useHistory();

	const formatDependencyCount = count => {
		if (!Number.isFinite(count) || count < 0) {
			return '0';
		}

		return count > 99 ? '100+' : `${count}`;
	};

	const getSubmissionStatusFromResponse = response => {
		if (response.data.payload === false) {
			let displayMessage = 'Submitted Unsuccessfully';
			if (
				response?.data?.messages &&
				response.data.messages.length > 0 &&
				response.data.messages[0].Value
			) {
				displayMessage = response.data.messages[0].Value;
			}

			return {
				error: true,
				submitted: false,
				message: displayMessage,
			};
		}

		return {
			error: false,
			submitted: true,
			message: 'Submitted Successfully',
		};
	};

	const submitStandardUpdate = async (values, actions) => {
		try {
			const response = await submitJobCategoryForm(
				{
					...new JobCategoryDTO(values, isNew).prepare(
						maintenanceInformation,
						isNew,
					),
				},
				isNew,
			);
			setSubmissionStatus(getSubmissionStatusFromResponse(response));
		} catch {
			setSubmissionStatus({
				error: true,
				submitted: false,
				message: 'Submission was unsuccessful',
			});
		} finally {
			actions.setSubmitting(false);
		}
	};

	const cancelDeactivateDialog = formik => {
		setDeactivateDialogOpen(false);
		setPendingValues(null);
		setIsDeactivating(false);
		formik.setSubmitting(false);
		formik.setFieldValue(JOB_CATEGORY_KEYS.ACTIVE, true, false);
		setDependencySummaryError(false);
	};

	return (
		<>
			{jobCategory && (
				<Formik
					initialValues={{
						...jobCategory?.extractValues(),
					}}
					validationSchema={Yup.object().shape({
						[JOB_CATEGORY_KEYS.ACTIVE]: Yup.boolean(),
						[JOB_CATEGORY_KEYS.ID]: Yup.number()
							.integer('ID must be an integer')
							.typeError('ID must be a number')
							.positive('ID must be a positive number')
							.required('Job Category ID is required.')
							.test('Unique Key', async function (id) {
								if (!isNew) {
									return true;
								}
								if (id) {
									if (Number.isInteger(id)) {
										const result = await getJobCategory(id);
										if (
											result &&
											result?.jobCategoryID === id
										) {
											return this.createError({
												message: 'ID already in use.',
											});
										} else {
											return true;
										}
									} else {
										return this.createError({
											message:
												'Job Category ID must be an integer',
										});
									}
								} else {
									return this.createError({
										message: 'Job Category ID is required.',
									});
								}
							}),
						[JOB_CATEGORY_KEYS.NAME]: Yup.string()
							.maxCounter(255)
							.required('Job Category Name is required.')
							.test('Unique Name', async function (value) {
								if (value) {
									const sanitizedValue = value
										.trim()
										.toLocaleLowerCase();
									const results = await searchJobCategories(
										sanitizedValue,
										true,
										true,
									);
									if (results && results?.length > 0) {
										let exists = results.some(
											jc =>
												jc.label
													?.trim()
													.toLocaleLowerCase() ===
													sanitizedValue &&
												jc.id !==
													this.parent[
														JOB_CATEGORY_KEYS.ID
													],
										);
										if (exists) {
											return this.createError({
												message: 'Name already in use.',
											});
										} else {
											return true;
										}
									} else {
										return true;
									}
								} else {
									return this.createError({
										message:
											'Job Category Name is required.',
									});
								}
							}),
						[JOB_CATEGORY_KEYS.DESCRIPTION]:
							Yup.string().nullable(),
						[JOB_CATEGORY_KEYS.TYPE]: Yup.string()
							.oneOf(JOB_CATEGORY_TYPE_OPTIONS)
							.required(),
						[JOB_CATEGORY_KEYS.APP_ID]: Yup.number()
							.oneOf([OBJ_APP_BRIDGES.ID, OBJ_APP_HP.ID])
							.required(),
						[JOB_CATEGORY_KEYS.OWNING_APPLICATION]: Yup.string()
							.maxCounter(255)
							.nullable(),
						[JOB_CATEGORY_KEYS.TEMPLATE_ID]: Yup.string()
							.maxCounter(255)
							.notOneOf(
								[
									'NA',
									'N/A',
									'na',
									'n/a',
									'NONE',
									'None',
									'none',
									'0',
								],
								'Leave blank if the job category has no template or is not known.',
							)
							.nullable()
							.when([JOB_CATEGORY_KEYS.TEMPLATE_NAME], {
								is: value => !!value,
								then: Yup.string().required(
									'You have specified a Template Name but not an ID',
								),
							}),
						[JOB_CATEGORY_KEYS.TEMPLATE_NAME]: Yup.string()
							.maxCounter(255)
							.nullable(),
						[JOB_CATEGORY_KEYS.SUBTEMPLATE_IDS]: Yup.string()
							.maxCounter(255)
							.notOneOf(
								[
									'NA',
									'N/A',
									'na',
									'n/a',
									'NONE',
									'None',
									'none',
									'0',
								],
								'Leave blank if the job category has no subtemplate or is not known.',
							)
							.nullable()
							.when([JOB_CATEGORY_KEYS.SUBTEMPLATE_NAMES], {
								is: value => !!value,
								then: Yup.string().required(
									'You have specified a Subtemplate Name but not an ID',
								),
							}),
						[JOB_CATEGORY_KEYS.SUBTEMPLATE_NAMES]: Yup.string()
							.maxCounter(255)
							.nullable(),
						[JOB_CATEGORY_KEYS.IS_PROVIDER]:
							Yup.boolean().nullable(),
						[JOB_CATEGORY_KEYS.SER]: Yup.boolean().required(),
						[JOB_CATEGORY_KEYS.SER_BLUEPRINT_ID]: Yup.string()
							.maxCounter(255)
							.test('SER Check', async function (value) {
								if (
									this.parent[JOB_CATEGORY_KEYS.SER] !==
										true &&
									!!value
								) {
									return this.createError({
										message:
											'You indicated that this job category does not require an SER but provided an SER Blueprint ID.',
									});
								}
								return true;
							}),
						[JOB_CATEGORY_KEYS.TRAINING_TRACK1]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable(),
						[JOB_CATEGORY_KEYS.TRAINING_TRACK2]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable(),
						[JOB_CATEGORY_KEYS.TRAINING_TRACK3]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable(),
						[JOB_CATEGORY_KEYS.TRAINING_TRACK4]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable(),
						[JOB_CATEGORY_KEYS.TRAINING_TRACK5]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable(),
						[JOB_CATEGORY_KEYS.TRAINING_TRACK6]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable(),
						[JOB_CATEGORY_KEYS.TRAINING_TRACK7]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable(),
						[JOB_CATEGORY_KEYS.TRAINING_TRACK8]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable(),
						[JOB_CATEGORY_KEYS.DAY_IN_THE_LIFE_ACTIVITY_PRE]:
							Yup.string().maxCounter(255).nullable(),
						[JOB_CATEGORY_KEYS.DAY_IN_THE_LIFE_ACTIVITY_POST]:
							Yup.string().maxCounter(255).nullable(),
						[JOB_CATEGORY_KEYS.NOTE]: Yup.string().nullable(),
					})}
					validateOnMount
					onSubmit={async (values, actions) => {
						actions.setSubmitting(true);

						const isDeactivationRequest =
							!isNew &&
							jobCategory?.active === true &&
							values[JOB_CATEGORY_KEYS.ACTIVE] === false;

						if (isDeactivationRequest) {
						setPendingValues(values);

						try {
							const dependencies =
								await getJobCategoryDependencies(
									values[JOB_CATEGORY_KEYS.ID],
								);
							setDependencySummary({
								mappingCount:
									dependencies?.mappingCount ?? 0,
								userAssignmentCount:
									dependencies?.userAssignmentCount ?? 0,
							});
							setDependencySummaryError(false);
							} catch {
								setDependencySummaryError(true);
								setDependencySummary({
									mappingCount: 0,
									userAssignmentCount: 0,
								});
							}

							setDeactivateDialogOpen(true);
							actions.setSubmitting(false);
							return;
						}

						await submitStandardUpdate(values, actions);
					}}
				>
					{formik => {
						return (
							<Form>
							{sSubmissionStatus?.message && (
								<Alert
									severity={
										sSubmissionStatus?.error
											? 'error'
											: 'success'
									}
									variant='filled'
									onClose={() => {
										setSubmissionStatus(
											prev => (prev.message = ''),
										);
									}}
								>
									{sSubmissionStatus?.message}
								</Alert>
							)}
							<Dialog
								open={sIsDeactivateDialogOpen}
								onClose={() => {
									cancelDeactivateDialog(formik);
								}}
							>
								<DialogTitle>
									Confirm Job Category Deactivation
								</DialogTitle>
								<DialogContent>
									<DialogContentText>
										This job category will be deactivated despite dependencies remaining.
										Please be advised that mappings and users referencing this job category are not automatically updated and should be reviewed below.
									</DialogContentText>
									<br/>
									{sDependencySummaryError ? (
										<DialogContentText>
											Dependency counts are currently unavailable.
											You can still proceed, but existing mappings and
											assigned users will remain unchanged.
											<i>Consider updating mappings and user assignments before or immediately after deactivation to avoid leaving orphaned mappings or users assigned to an inactive job category.</i>
											<br/>
										</DialogContentText>
									) : (
										<>
											<DialogContentText>
												<strong>
													Mappings still pointing to this job category:{' '}
													{formatDependencyCount(
														sDependencySummary.mappingCount,
													)}
												</strong>
												<br/>
												<i>Future users may still be pre-assigned to this job category even after it is deactivated.</i>
											</DialogContentText>
											<br/>
											<DialogContentText>
												<strong>
													Users still assigned to this job category:{' '}
													{formatDependencyCount(
														sDependencySummary.userAssignmentCount,
													)}
												</strong>
												<br/>
												<i>Current users assigned to this job category will remain assigned even after it is deactivated.</i>
											</DialogContentText>
										</>
									)}
								</DialogContent>
								<DialogActions>
									<Button
										onClick={() => {
											cancelDeactivateDialog(formik);
										}}
										variant='contained'
									>
										Cancel
									</Button>
									<Button
										color='error'
										variant='contained'
										disabled={sIsDeactivating}
										onClick={async () => {
											if (!sPendingValues) {
												cancelDeactivateDialog(formik);
												return;
											}

											const pendingValues = sPendingValues;
											setPendingValues(null);
											setDeactivateDialogOpen(false);
											formik.setSubmitting(true);
											setIsDeactivating(true);

											try {
												await submitStandardUpdate(
													pendingValues,
													formik,
												);
											} catch {
												setSubmissionStatus({
													error: true,
													submitted: false,
													message:
														'Submission was unsuccessful',
												});
												formik.setSubmitting(false);
											} finally {
												setIsDeactivating(false);
												setDependencySummaryError(false);
											}
										}}
									>
										{sIsDeactivating ? 'Deactivating...' : 'Continue'}
									</Button>
								</DialogActions>
							</Dialog>
							<Stack
								sx={{ marginTop: '32px' }}
								direction='column'
								spacing={{
									xs: 0.5,
									sm: 0.5,
									md: 1,
								}}
							>
								{!isNew && (
									<>
										<FormControlLabel
											control={
												<Switch
													defaultChecked
													name={
														JOB_CATEGORY_KEYS.ACTIVE
													}
													id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_SWITCH}--${JOB_CATEGORY_KEYS.ACTIVE}`}
													checked={
														formik.values[
															JOB_CATEGORY_KEYS
																.ACTIVE
														]
													}
													onChange={
															async event => {
																formik.handleChange(event);
															}
													}
													onBlur={formik.handleBlur}
													disabled={
															sSubmissionStatus?.submitted ||
															sIsDeactivating
													}
												/>
											}
											label={
												formik.values[
													JOB_CATEGORY_KEYS.ACTIVE
												]
													? 'Active'
													: 'Inactive'
											}
										/>
									</>
								)}
								<FormControlCustomized>
									<TextField
										name={JOB_CATEGORY_KEYS.ID}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.ID}`}
										label={
											<>
												<RequiredIndicator />
												Job Category ID
											</>
										}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[JOB_CATEGORY_KEYS.ID]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.ID
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.ID
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS.ID
											] &&
											formik.errors[JOB_CATEGORY_KEYS.ID]
										}
										disabled={
											sSubmissionStatus?.submitted ||
											!isNew
										}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={JOB_CATEGORY_KEYS.NAME}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.NAME}`}
										label={
											<>
												<RequiredIndicator />
												Name
											</>
										}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS.NAME
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.NAME
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.NAME
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS.NAME
											] &&
											formik.errors[
												JOB_CATEGORY_KEYS.NAME
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={JOB_CATEGORY_KEYS.DESCRIPTION}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.DESCRIPTION}`}
										label='Description'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS.DESCRIPTION
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.DESCRIPTION
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.DESCRIPTION
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS.DESCRIPTION
											] &&
											formik.errors[
												JOB_CATEGORY_KEYS.DESCRIPTION
											]
										}
										disabled={sSubmissionStatus?.submitted}
										multiline
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<InputLabel id='type-select-label'>
										<>
											<RequiredIndicator />
											Type
										</>
									</InputLabel>
									<Select
										labelId='type-select-label'
										name={JOB_CATEGORY_KEYS.TYPE}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_SELECT}--${JOB_CATEGORY_KEYS.TYPE}`}
										label='Type'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS.TYPE
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.TYPE
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.TYPE
											]
										}
										disabled={sSubmissionStatus?.submitted}
									>
										<MenuItem
											value=''
											key={'type-option-0'}
										>
											<em>None</em>
										</MenuItem>
										{JOB_CATEGORY_TYPE_OPTIONS.map(
											(option, index) => (
												<MenuItem
													value={option}
													key={
														'type-option-' +
														index +
														1
													}
												>
													{option}
												</MenuItem>
											),
										)}
									</Select>
									<FormHelperText
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.TYPE
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.TYPE
											]
										}
									>
										{formik.touched[
											JOB_CATEGORY_KEYS.TYPE
										] &&
											formik.errors[
												JOB_CATEGORY_KEYS.TYPE
											]}
									</FormHelperText>
								</FormControlCustomized>
								<FormControlCustomized>
									<InputLabel id='system-select-label'>
										<>
											<RequiredIndicator />
											System
										</>
									</InputLabel>
									<Select
										labelId='system-select-label'
										name={JOB_CATEGORY_KEYS.APP_ID}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_SELECT}--${JOB_CATEGORY_KEYS.APP_ID}`}
										label='System'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS.APP_ID
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.APP_ID
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.APP_ID
											]
										}
										disabled={sSubmissionStatus?.submitted}
									>
										{[OBJ_APP_BRIDGES, OBJ_APP_HP].map(
											(option, index) => (
												<MenuItem
													value={option.ID}
													key={
														'type-option-' +
														index +
														1
													}
												>
													{option.Name}
												</MenuItem>
											),
										)}
									</Select>
									<FormHelperText
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.APP_ID
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.APP_ID
											]
										}
									>
										{formik.touched[
											JOB_CATEGORY_KEYS.APP_ID
										] &&
											formik.errors[
												JOB_CATEGORY_KEYS.APP_ID
											]}
									</FormHelperText>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={
											JOB_CATEGORY_KEYS.OWNING_APPLICATION
										}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.OWNING_APPLICATION}`}
										label='Owning Application'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS
													.OWNING_APPLICATION
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS
													.OWNING_APPLICATION
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS
													.OWNING_APPLICATION
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS
													.OWNING_APPLICATION
											] &&
											formik.errors[
												JOB_CATEGORY_KEYS
													.OWNING_APPLICATION
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={JOB_CATEGORY_KEYS.TEMPLATE_ID}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.TEMPLATE_ID}`}
										label='Template ID'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS.TEMPLATE_ID
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.TEMPLATE_ID
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.TEMPLATE_ID
											]
										}
										helperText={
											formik.errors[
												JOB_CATEGORY_KEYS.TEMPLATE_ID
											] ??
											(formik.values[
												JOB_CATEGORY_KEYS.TEMPLATE_ID
											]
												? ''
												: 'To not disrupt downstream automation, leave blank if unknown or not applicable.')
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={JOB_CATEGORY_KEYS.TEMPLATE_NAME}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.TEMPLATE_NAME}`}
										label='Template Name'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS.TEMPLATE_NAME
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.TEMPLATE_NAME
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.TEMPLATE_NAME
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS.TEMPLATE_NAME
											] &&
											formik.errors[
												JOB_CATEGORY_KEYS.TEMPLATE_NAME
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={JOB_CATEGORY_KEYS.SUBTEMPLATE_IDS}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.SUBTEMPLATE_IDS}`}
										label='Subtemplate ID(s)'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_IDS
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_IDS
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_IDS
											]
										}
										helperText={
											formik.errors[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_IDS
											] ??
											(formik.values[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_IDS
											]
												? ''
												: 'To not disrupt downstream automation, leave blank if unknown or not applicable.')
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={
											JOB_CATEGORY_KEYS.SUBTEMPLATE_NAMES
										}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.SUBTEMPLATE_NAMES}`}
										label='Subtemplate Name(s)'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_NAMES
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_NAMES
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_NAMES
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_NAMES
											] &&
											formik.errors[
												JOB_CATEGORY_KEYS
													.SUBTEMPLATE_NAMES
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<InputLabel
										id='is-provider-select-label'
										sx={{ backgroundColor: '#fff', mt: 1 }}
									>
										Is Provider?
									</InputLabel>
									<Select
										labelId='is-provider-select-label'
										name={JOB_CATEGORY_KEYS.IS_PROVIDER}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_SELECT}--${JOB_CATEGORY_KEYS.IS_PROVIDER}`}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS.IS_PROVIDER
											]
										}
										disabled={sSubmissionStatus?.submitted}
										sx={{ mt: 1 }}
									>
										<MenuItem value=''>
											<em>None</em>
										</MenuItem>
										<MenuItem value={false}>No</MenuItem>
										<MenuItem value={true}>Yes</MenuItem>
									</Select>
									<FormHelperText
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.IS_PROVIDER
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.IS_PROVIDER
											]
										}
									>
										{formik.touched[
											JOB_CATEGORY_KEYS.IS_PROVIDER
										] &&
											formik.errors[
												JOB_CATEGORY_KEYS.IS_PROVIDER
											]}
									</FormHelperText>
								</FormControlCustomized>
								<FormControlCustomized>
									<InputLabel
										id='ser-select-label'
										sx={{ backgroundColor: '#fff', mt: 1 }}
									>
										<>
											<RequiredIndicator />
											Requires SER
										</>
									</InputLabel>
									<Select
										labelId='ser-select-label'
										name={JOB_CATEGORY_KEYS.SER}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_SELECT}--${JOB_CATEGORY_KEYS.SER}`}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[JOB_CATEGORY_KEYS.SER]
										}
										disabled={sSubmissionStatus?.submitted}
										sx={{ mt: 1 }}
									>
										<MenuItem value=''>
											<em>None</em>
										</MenuItem>
										<MenuItem value={false}>No</MenuItem>
										<MenuItem value={true}>Yes</MenuItem>
									</Select>
									<FormHelperText
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.SER
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.SER
											]
										}
									>
										{formik.touched[
											JOB_CATEGORY_KEYS.SER
										] &&
											formik.errors[
												JOB_CATEGORY_KEYS.SER
											]}
									</FormHelperText>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={
											JOB_CATEGORY_KEYS.SER_BLUEPRINT_ID
										}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.SER_BLUEPRINT_ID}`}
										label='What is the SER Blueprint ID that should be applied to this Job Category?'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS
													.SER_BLUEPRINT_ID
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS
													.SER_BLUEPRINT_ID
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS
													.SER_BLUEPRINT_ID
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS
													.SER_BLUEPRINT_ID
											] &&
											formik.errors[
												JOB_CATEGORY_KEYS
													.SER_BLUEPRINT_ID
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								{Array(TRAINING_TRACK_MAX)
									.fill(0)
									.map((_, index) => {
										const indexTrack = index + 1;
										return (
											<FormControlCustomized>
												<TrainingTrackAsyncAutocomplete
													key={
														`trainingtrackauto-${indexTrack}-` +
														sKey
													}
													name={
														JOB_CATEGORY_KEYS[
															`TRAINING_TRACK${indexTrack}`
														]
													}
													id={`${
														BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY
													}__${
														BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD
													}--${
														JOB_CATEGORY_KEYS[
															`TRAINING_TRACK${indexTrack}`
														]
													}`}
													label={
														'Training Track ' +
														indexTrack
													}
													onchange={value => {
														formik.setFieldValue(
															JOB_CATEGORY_KEYS[
																`TRAINING_TRACK${indexTrack}`
															],
															value,
														);
													}}
													onBlur={() =>
														formik.setFieldTouched(
															JOB_CATEGORY_KEYS[
																`TRAINING_TRACK${indexTrack}`
															],
															true,
														)
													}
													value={
														formik.values[
															JOB_CATEGORY_KEYS[
																`TRAINING_TRACK${indexTrack}`
															]
														]
													}
													error={
														!!formik.touched[
															JOB_CATEGORY_KEYS[
																`TRAINING_TRACK${indexTrack}`
															]
														] &&
														!!formik.errors[
															JOB_CATEGORY_KEYS[
																`TRAINING_TRACK${indexTrack}`
															]
														]
													}
													helperText={
														formik.touched[
															JOB_CATEGORY_KEYS[
																`TRAINING_TRACK${indexTrack}`
															]
														] &&
														formik.errors[
															JOB_CATEGORY_KEYS[
																`TRAINING_TRACK${indexTrack}`
															]
														]
													}
													disabled={
														sSubmissionStatus?.submitted
													}
												/>
											</FormControlCustomized>
										);
									})}
								<FormControlCustomized>
									<TextField
										name={
											JOB_CATEGORY_KEYS.DAY_IN_THE_LIFE_ACTIVITY_PRE
										}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.DAY_IN_THE_LIFE_ACTIVITY_PRE}`}
										label='Day in the life activity (pre go-live)'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_PRE
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_PRE
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_PRE
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_PRE
											] &&
											formik.errors[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_PRE
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={
											JOB_CATEGORY_KEYS.DAY_IN_THE_LIFE_ACTIVITY_POST
										}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.DAY_IN_THE_LIFE_ACTIVITY_POST}`}
										label='Day in the life activity (post go-live)'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_POST
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_POST
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_POST
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_POST
											] &&
											formik.errors[
												JOB_CATEGORY_KEYS
													.DAY_IN_THE_LIFE_ACTIVITY_POST
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={JOB_CATEGORY_KEYS.NOTE}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_KEYS.NOTE}`}
										label='Note'
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												JOB_CATEGORY_KEYS.NOTE
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_KEYS.NOTE
											] &&
											!!formik.errors[
												JOB_CATEGORY_KEYS.NOTE
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_KEYS.NOTE
											] &&
											formik.errors[
												JOB_CATEGORY_KEYS.NOTE
											]
												? formik.errors[
														JOB_CATEGORY_KEYS.NOTE
													]
												: 'Notes support a limited version of markdown syntax. A preview will be rendered below.'
										}
										disabled={sSubmissionStatus?.submitted}
										multiline
										minRows={3}
									/>
								</FormControlCustomized>
								{formik.values[JOB_CATEGORY_KEYS.NOTE] && (
									<FormControlCustomized>
										<Box
											sx={{
												border: 'solid',
												borderWidth: '1px',
												borderRadius: '4px',
												borderColor: 'lightgray',
												p: 2,
											}}
										>
											<InputLabel
												sx={{
													backgroundColor: 'white',
													pl: 1,
													pr: 1,
												}}
												shrink
											>
												Note Preview
											</InputLabel>
											<ReactMarkdown
												components={{
													a: props => (
														<a
															href={props.href}
															target='_blank'
															rel='noreferrer'
														>
															{props.children}
														</a>
													),
												}}
											>
												{
													formik.values[
														JOB_CATEGORY_KEYS.NOTE
													]
												}
											</ReactMarkdown>
										</Box>
									</FormControlCustomized>
								)}
								<Button
									id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--${BEM_NAME_ELEMENTS.SUBMIT}`}
									type='submit'
									variant={'contained'}
									disabled={
										(!!formik.errors &&
											Object.keys(formik.errors).length > 0) ||
										!formik.dirty ||
										formik.isSubmitting ||
										sIsDeactivating ||
										sSubmissionStatus?.submitted
									}
								>
									{formik.isSubmitting
										? 'Submitting...'
										: sSubmissionStatus?.submitted
											? 'Submitted'
											: isNew
												? 'Create'
												: 'Save'}
								</Button>
								{sSubmissionStatus?.submitted && (
									<>
										{isNew && (
											<Button
												id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--new`}
												variant={'contained'}
												color={'success'}
												onClick={() => {
													setKey(nanoid());
													formik.resetForm({
														...jobCategory.extractValues(),
													});
													setSubmissionStatus(null);
												}}
											>
												Submit New Job Category
											</Button>
										)}
										<Button
											id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--home`}
											variant={'contained'}
											color={'primary'}
											onClick={() =>
												history.push('/EpicUtilities/Home')
											}
										>
											Back to Epic Administration Home
										</Button>
									</>
								)}
							</Stack>
						</Form>
						);
					}}
				</Formik>
			)}
		</>
	);
};
export default FormView;
